import type { UseFormReturn } from "react-hook-form";
import type { ReactNode } from "react";
import { Checkbox } from "@/components/ui/checkbox";
import { Button } from "@/components/ui/button";
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { PhoneInput } from "@/components/Inputs/PhoneInput";
import { HealthcareProfessionalTypeSelect } from "@/components/Selects/HealthcareProfessionalTypeSelect";
import { ProviderNpiSelect } from "@/components/Selects/ProviderNpiSelect";
import { SpecialtiesSelect } from "@/components/Selects/SpecialtiesSelect";
import type { ProviderFormValues } from "./provider-form-schema";

type InviteProviderFieldsProps = {
  form: UseFormReturn<ProviderFormValues>;
};

export function ProviderContactFields({
  form,
  phoneFirst = false,
}: InviteProviderFieldsProps & { phoneFirst?: boolean }) {
  const email = (
    <FormField
      control={form.control}
      name="email"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Email (optional)</FormLabel>
          <FormControl>
            <Input type="email" placeholder="provider@example.com" {...field} />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
  const phone = (
    <FormField
      control={form.control}
      name="phone"
      render={({ field }) => (
        <FormItem>
          <FormLabel>Phone (optional)</FormLabel>
          <FormControl>
            <PhoneInput
              placeholder="(555) 555-5555"
              value={field.value ?? ""}
              onChange={field.onChange}
            />
          </FormControl>
          <FormMessage />
        </FormItem>
      )}
    />
  );
  return phoneFirst ? (
    <>
      {phone}
      {email}
    </>
  ) : (
    <>
      {email}
      {phone}
    </>
  );
}

/**
 * The details themselves, without the NPI-lookup step that precedes them when a
 * provider is first added — editing a roster row starts from values that are
 * already filled in, and re-running a lookup would overwrite the organization's
 * own corrections.
 */
export function ProviderDetailFields({
  form,
  afterNpi,
}: InviteProviderFieldsProps & { afterNpi?: ReactNode }) {
  return (
    <>
      <FormField
        control={form.control}
        name="npi"
        render={({ field }) => (
          <FormItem>
            <FormLabel>NPI (optional)</FormLabel>
            <FormControl>
              <Input
                placeholder="10-digit NPI"
                inputMode="numeric"
                maxLength={10}
                {...field}
              />
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />

      {afterNpi}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <FormField
          control={form.control}
          name="firstName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>First name (optional)</FormLabel>
              <FormControl>
                <Input placeholder="Jane" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <FormField
          control={form.control}
          name="lastName"
          render={({ field }) => (
            <FormItem>
              <FormLabel>Last name (optional)</FormLabel>
              <FormControl>
                <Input placeholder="Doe" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      </div>

      <FormField
        control={form.control}
        name="healthcareProfessionalType"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Professional type (optional)</FormLabel>
            <HealthcareProfessionalTypeSelect
              isMulti={false}
              value={field.value ?? undefined}
              onChange={(value) =>
                field.onChange(
                  value && !Array.isArray(value) ? value : undefined,
                )
              }
            />
            <FormMessage />
          </FormItem>
        )}
      />

      <FormField
        control={form.control}
        name="specialtyCodes"
        render={({ field }) => (
          <FormItem>
            <FormLabel>Specialties (optional)</FormLabel>
            <SpecialtiesSelect
              isMulti={true}
              value={field.value ?? []}
              onChange={(value) =>
                field.onChange(Array.isArray(value) ? value : [])
              }
            />
            <FormMessage />
          </FormItem>
        )}
      />
    </>
  );
}

type ProviderIdentityFieldsProps = InviteProviderFieldsProps & {
  manualEntry: boolean;
  providerNpi: ProviderFormValues["providerNpi"];
  onManualEntryChange: (manualEntry: boolean) => void;
  afterNpi?: ReactNode;
};

export function ProviderIdentityFields({
  form,
  manualEntry,
  providerNpi,
  onManualEntryChange,
  afterNpi,
}: ProviderIdentityFieldsProps) {
  return (
    <>
      {!manualEntry && (
        <div className="space-y-1">
          <FormField
            control={form.control}
            name="providerNpi"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Look up provider by NPI</FormLabel>
                <FormControl>
                  <ProviderNpiSelect
                    value={field.value ?? undefined}
                    onChange={field.onChange}
                    placeholder="Search by name or NPI number"
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
          {!providerNpi && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-muted-foreground"
              onClick={() => onManualEntryChange(true)}
            >
              Enter NPI details manually
            </Button>
          )}
        </div>
      )}

      {!manualEntry && afterNpi}
      {(manualEntry || providerNpi) && (
        <div className="space-y-4">
          {manualEntry && (
            <Button
              type="button"
              variant="link"
              size="sm"
              className="h-auto p-0 text-muted-foreground"
              onClick={() => onManualEntryChange(false)}
            >
              Search by NPI instead
            </Button>
          )}

          <ProviderDetailFields
            form={form}
            afterNpi={manualEntry ? afterNpi : undefined}
          />
        </div>
      )}
    </>
  );
}

export function SendInvitationField({ form }: InviteProviderFieldsProps) {
  return (
    <FormField
      control={form.control}
      name="sendInvitation"
      render={({ field }) => (
        <FormItem className="flex items-start gap-3 rounded-md border p-3">
          <FormControl>
            <Checkbox
              checked={field.value}
              onCheckedChange={(checked) => field.onChange(checked === true)}
            />
          </FormControl>
          <div className="space-y-1">
            <FormLabel className="cursor-pointer">
              Send invitation now
            </FormLabel>
            <p className="text-sm text-muted-foreground">
              Email the provider so they can claim access and sign in. You can
              invite them later.
            </p>
            <FormMessage />
          </div>
        </FormItem>
      )}
    />
  );
}
